import { Plus } from "lucide-react";
import { PLANS, INCLUDED, CURRENCY } from "@/data/studio";
import { Eyebrow, H2, Pill, Reveal } from "./ui";

const Plan = ({ p, i }) => (
  <Reveal delay={i * 0.06} data-testid={`studio-plan-${i + 1}`}
    className={`relative flex flex-col overflow-hidden rounded border p-7 sm:p-8 ${p.featured ? "border-brand/60 bg-coal" : "border-white/10 bg-sink"}`}>
    {p.featured && <>
      <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand via-fuchsia-400 to-brand" />
      <span aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand/30 blur-3xl" />
    </>}
    <div className="relative flex items-center justify-between">
      <span className="font-mono text-[11px] text-brand">{String(i + 1).padStart(2, "0")}</span>
      {p.featured && <span className="rounded-full bg-brand px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">Most Popular</span>}
    </div>
    <h3 className="relative mt-8 font-display text-2xl font-bold text-white">{p.name}</h3>
    <p className="relative mt-2 text-[15px] text-zinc-400">{p.dur}</p>
    <p className={`relative mt-10 font-display font-extrabold tracking-tight text-white ${p.price ? "text-5xl" : "text-4xl"}`} data-testid={`studio-plan-price-${i + 1}`}>
      {p.price ? `${CURRENCY}${p.price.toLocaleString("en-GB")}` : "Get a Quote"}
    </p>
    <div className="relative mt-auto pt-10">
      <Pill label={p.cta} variant={p.featured ? "pink" : "outline"} testid={`studio-plan-cta-${i + 1}`} className="w-full !px-4" />
    </div>
  </Reveal>
);

export const Pricing = () => (
  <section id="pricing" data-testid="studio-pricing" className="border-t border-white/10 bg-sink py-24 sm:py-32 lg:py-40">
    <div className="mx-auto max-w-[1480px] px-5 sm:px-8 lg:px-12">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>Pricing</Eyebrow></Reveal>
          <Reveal delay={0.05}><H2 className="mt-6">Straightforward <span className="text-brand">Production Pricing.</span></H2></Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9">
          <p className="text-base leading-relaxed text-zinc-400 sm:text-lg">Fixed productions for set durations, or a fully custom scope — every project is confirmed and priced before production begins.</p>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {PLANS.map((p, i) => <Plan key={p.name} p={p} i={i} />)}
      </div>

      <Reveal className="mt-8 rounded border border-white/10 bg-coal p-7 sm:p-12" data-testid="studio-included">
        <h3 className="font-display text-3xl font-bold text-white sm:text-4xl">Everything Included in Every Production.</h3>
        <p className="mt-3 text-base text-zinc-400">Every Perfect Digitals AI video production includes:</p>
        <ul className="mt-10 grid gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {INCLUDED.map((it) => (
            <li key={it} className="flex items-start gap-3 text-[15px] text-zinc-200"><Plus className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{it}</li>
          ))}
        </ul>
      </Reveal>

      <div className="mt-12 grid gap-8 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-8">
          <p className="text-lg leading-relaxed text-zinc-200">The quality of production does not change between packages. Pricing is based primarily on the finished duration of the video.</p>
          <p className="mt-4 max-w-3xl text-[13px] leading-relaxed text-zinc-400">Highly specialised or unusually complex production requirements may require a custom quotation. This can include extensive character consistency, complex product recreation, multiple-language productions, large campaign requirements or other bespoke production needs.</p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-4 lg:flex lg:justify-end">
          <Pill label="Scope Your Project" testid="studio-pricing-cta" />
        </Reveal>
      </div>
    </div>
  </section>
);
